<template>
  <FormButton
    iconName="personRunning"
    @click="handleNewRunClick"
  >
    Schedule the next run
  </FormButton>
</template>

<script setup lang="ts">
import FormButton from "@/components/FormButton.vue";
import { RouteName } from "@/lib/router/routes";
import getMostCommonDayOfWeekForRuns from "@/lib/utils/date/getMostCommonDayOfWeekForRuns";
import useModalStore from "@/stores/useModalStore";
import { TZDate } from "@date-fns/tz";
import { Season, SeasonWithMaybeCourt, SeasonWithMaybeCrew, SeasonWithRuns } from "@shared/lib/database/query.types";
import { addDays, addMinutes, format, nextFriday, nextMonday, nextSaturday, nextSunday, nextThursday, nextTuesday, nextWednesday } from "date-fns";
import { ScheduleRunModalHistoryState } from "./modals/ScheduleRunModalView.types";

const {
  season,
} = defineProps<{
  season: SeasonWithMaybeCrew & SeasonWithMaybeCourt & SeasonWithRuns,
}>();

const {
  openModal,
} = useModalStore();

function getNextRunDateForSeason (season: SeasonWithMaybeCourt & SeasonWithRuns) {
  const dateString = season.end_on || format(addDays(new Date(), 2), "yyyy-MM-dd");
  const time = season.default_start_time || "08:00:00";
  const timezone = season.court?.timezone || "America/New_York";
  const date = new TZDate(`${dateString}T${time}`, timezone);

  switch (getMostCommonDayOfWeekForRuns(season.runs, timezone)) {
    case 0:
      return nextSunday(date);
    case 1:
      return nextMonday(date);
    case 2:
      return nextTuesday(date);
    case 3:
      return nextWednesday(date);
    case 4:
      return nextThursday(date);
    case 5:
      return nextFriday(date);
    case 6:
      return nextSaturday(date);
    default:
      return date;
  }
}

function getRunDurationForSeason (season: Season) {
  const [startHour, startMinute] = season.default_start_time?.split(":").map(parseFloat) || [8, 0];
  const [endHour, endMinute] = season.default_end_time?.split(":").map(parseFloat) || [null, null];

  if (endHour === null || endMinute === null) return null;

  return (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
}

function handleNewRunClick () {
  const startDate = getNextRunDateForSeason(season);
  const duration = getRunDurationForSeason(season);
  const endDate = startDate && duration
    ? addMinutes(startDate, duration)
    : null;

  const state: ScheduleRunModalHistoryState = {
    crewIds: [season.crew_id],
    run: {
      season_id: season.id,
      court_id: season.default_court_id,
      start_at: startDate ? startDate.toISOString() : undefined,
      end_at: endDate ? endDate.toISOString() : undefined,
      min_player_count: season.default_min_player_count,
      max_player_count: season.default_max_player_count,
      rsvp_day_count: season.default_rsvp_day_count,
    },
  };

  openModal({
    name: RouteName.runNew,
  }, {
    state,
  });
}
</script>
